import { useEffect, useState } from 'react';
import { useError } from '@/context/ErrorContext';
import { X, AlertOctagon } from 'lucide-react';
import { createPortal } from 'react-dom';

export default function GlobalErrorModal() {
    const { error, title, clearError } = useError();
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        if (error) {
            setIsVisible(true);
        } else {
            const timer = setTimeout(() => setIsVisible(false), 200);
            return () => clearTimeout(timer);
        }
    }, [error]);

    if (!error && !isVisible) return null;

    return createPortal(
        <div
            className={`fixed inset-0 z-[100] flex items-center justify-center p-6 bg-background/80 backdrop-blur-md transition-all duration-200 ${error ? 'opacity-100' : 'opacity-0'}`}
            onClick={clearError}
        >
            <div
                className={`bg-card w-full max-w-lg rounded-[2rem] shadow-2xl border border-destructive/20 overflow-hidden transition-all duration-300 transform ${error ? 'scale-100 translate-y-0 opacity-100' : 'scale-95 translate-y-4 opacity-0'}`}
                onClick={e => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
            >
                <div className="relative p-8 overflow-hidden">
                    <button
                        onClick={clearError}
                        className="absolute top-6 right-6 p-2 rounded-full bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground transition-all duration-200"
                        aria-label="Close error modal"
                    >
                        <X className="w-5 h-5" />
                    </button>

                    <div className="flex flex-col gap-6">
                        <div className="w-14 h-14 rounded-2xl bg-destructive/10 flex items-center justify-center shadow-inner border border-destructive/10">
                            <AlertOctagon className="w-7 h-7 text-destructive" strokeWidth={2.5} />
                        </div>

                        <div className="space-y-2">
                            <h3 className="text-2xl font-bold text-foreground tracking-tight">
                                {title || 'Something went wrong'}
                            </h3>
                            <div className="text-muted-foreground text-lg leading-relaxed font-medium opacity-90 whitespace-pre-line">
                                {error}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="p-6 bg-muted/30 border-t border-border flex justify-end">
                    <button
                        onClick={clearError}
                        className="px-8 py-3 bg-foreground text-background text-base font-bold rounded-xl hover:shadow-lg hover:-translate-y-0.5 transition-all active:scale-95 duration-200"
                    >
                        Dismiss
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}
